import {FC, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button} from '../../../components/Button/Button'
import {Input} from '../../../components/Input/Input'
import Swal from 'sweetalert2'
import {POST, PUT} from '../../../services/api'
import {Loading} from '../../../components/Loading/Loading'
import {useNavigate} from 'react-router-dom'
import {i18n} from '../../../translate/i18n'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import uuid from 'react-uuid'
import {ActivityCategory, AgeGroup, getCategoryLabel, getAgeGroupLabel, FormDataType} from './types'
import {EditorQuill} from '../../../components/EditorQuill/EditorQuill'
import {TitleFreePriceInput} from '../Comuns'

const CreateHotelActivityContent: FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<FormDataType>({
    title: '',
    description: '',
    period: '',
    time: '',
    location: '',
    capacity: '',
    category: ActivityCategory.ENTERTAINMENT,
    ageGroup: AgeGroup.FAMILY,
    price: '',
    titleFreePrice: '',
    order: 0,
    requiresReservation: false,
    isActive: true,
  })
  const [image, setImage] = useState<File | null>(null)

  const handleInputChange = (
    field: string,
    value: string | ActivityCategory | AgeGroup | boolean
  ) => {
    // Use functional update to avoid stale-closure overwrites
    // (Quill fires many rapid updates which could clobber other fields)
    setFormData((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      } as any

      // Se o campo alterado for 'price', limpar titleFreePrice quando preço for maior que 0
      if (field === 'price') {
        const num = Number(String(value))
        if (Number.isFinite(num) && num > 0) {
          updated.titleFreePrice = ''
        }
      }

      return updated
    })
  }

  const validateForm = () => {
    if (!formData.title.trim()) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_activities.msg_error_create')),
        text: String(i18n.t('hospitality_activities.msg_error_required_title')),
      })
      return false
    }
    if (formData.title.length > 255) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_activities.msg_error_create')),
        text: String(i18n.t('hospitality_activities.msg_error_title_length')),
      })
      return false
    }

    if (!formData.description.trim()) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_activities.msg_error_create')),
        text: String(i18n.t('hospitality_activities.msg_error_required_description')),
      })
      return false
    }

    if (!formData.time.trim()) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_activities.msg_error_create')),
        text: String(i18n.t('hospitality_activities.msg_error_required_time')),
      })
      return false
    }
    if (formData.time.length > 100) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_activities.msg_error_create')),
        text: String(i18n.t('hospitality_activities.msg_error_time_length')),
      })
      return false
    }

    if (!formData.location.trim()) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_activities.msg_error_create')),
        text: String(i18n.t('hospitality_activities.msg_error_required_location')),
      })
      return false
    }
    if (formData.location.length > 255) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_activities.msg_error_create')),
        text: String(i18n.t('hospitality_activities.msg_error_location_length')),
      })
      return false
    }

    if (!formData.capacity.trim()) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_activities.msg_error_create')),
        text: String(i18n.t('hospitality_activities.msg_error_required_capacity')),
      })
      return false
    }
    if (formData.capacity.length > 50) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_activities.msg_error_create')),
        text: String(i18n.t('hospitality_activities.msg_error_capacity_length')),
      })
      return false
    }

    if (formData.price && parseFloat(formData.price) < 0) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_activities.msg_error_create')),
        text: String(i18n.t('hospitality_activities.msg_error_price_negative')),
      })
      return false
    }

    if (
      formData.order === null ||
      formData.order === undefined ||
      String(formData.order).trim() === '' ||
      Number(formData.order) < 0
    ) {
      Swal.fire({
        icon: 'warning',
        title: String(i18n.t('hospitality_activities.msg_error_create')),
        text: String(i18n.t('hospitality_activities.msg_error_order_negative')),
      })
      return false
    }

    return true
  }

  const createActivity = async () => {
    if (!validateForm()) return

    try {
      setLoading(true)

      let imageRes = {
        success: false,
        image: '',
      }

      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        time: formData.time.trim(),
        location: formData.location.trim(),
        capacity: formData.capacity.trim(),
        image: imageRes.success ? imageRes.image : 'default/no-image.png',
        category: formData.category,
        ageGroup: formData.ageGroup,
        price: formData.price ? parseFloat(formData.price) : undefined,
        titleFreePrice: formData.titleFreePrice || null,
        requiresReservation: formData.requiresReservation,
        isActive: formData.isActive,
      }

      const response = await POST('/hotel-activities', payload)

      if (response.success) {
        // Upload da imagem se foi selecionada
        if (image) {
          const formData = new FormData()
          formData.append('file', image)
          formData.append('nameImage', `activity-${uuid()}.jpg`)
          formData.append('originalSize', 'true')
          imageRes = await POST('/upload-images/upload-one', formData)
          if (imageRes.success) {
            await PUT(`/hotel-activities/${response.data.id}`, {
              image: imageRes.image,
            })
          }
        }

        Swal.fire({
          icon: 'success',
          title: String(i18n.t('hospitality_activities.btn_create')),
          text: String(i18n.t('hospitality_activities.msg_success_create')),
        }).then(() => {
          navigate('/hospitality/hotel-activities')
        })
      } else {
        throw new Error(response.message || 'Erro ao criar atividade')
      }

      setLoading(false)
    } catch (e: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('hospitality_activities.msg_error_create')),
        text: String(e.message || i18n.t('hospitality_activities.msg_error_create')),
      })
      console.log(e)
    }
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('hospitality_activities.create_title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('hospitality_activities.create_subtitle')}
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text={i18n.t(`buttons.back`)}
                  color='info'
                  iconColor='#FFF'
                  icon='fas fa-arrow-left'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{
                    type: 'button',
                  }}
                  click={() => navigate('/hospitality/hotel-activities')}
                  btnClass='mb-5 fw-bolder me-3'
                />
              </div>
            </div>
            <div className='card-body'>
              <form>
                <div className='row'>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'default',
                      }}
                      label={i18n.t('hospitality_activities.field_title')}
                      placeholder={i18n.t('hospitality_activities.field_title_placeholder')}
                      maxCaracteres={255}
                      value={formData.title}
                      change={(value: string) => handleInputChange('title', value)}
                      smalltext={i18n.t('hospitality_activities.field_title_hint')}
                    />
                  </div>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'default',
                      }}
                      label={i18n.t('hospitality_activities.field_time')}
                      placeholder={i18n.t('hospitality_activities.field_time_placeholder')}
                      maxCaracteres={100}
                      value={formData.time}
                      change={(value: string) => handleInputChange('time', value)}
                      smalltext={i18n.t('hospitality_activities.field_time_hint')}
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-md-10'>
                    <EditorQuill
                      value={formData.description}
                      label={i18n.t('hospitality_activities.field_description')}
                      placeholder={i18n.t('hospitality_activities.field_description_placeholder')}
                      change={(value: string) => handleInputChange('description', value)}
                      maxLength={5000} // Limite de 5000 caracteres
                    />
                  </div>
                  <div className='col-md-2'>
                    <Input
                      inputAttr={{
                        type: 'image',
                      }}
                      label={i18n.t('hospitality_activities.field_image')}
                      value={image}
                      change={(value: React.BaseSyntheticEvent) =>
                        setImage(value ? value.target.files[0] : null)
                      }
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-md-12'>
                    <Input
                      inputAttr={{
                        type: 'default',
                      }}
                      label={i18n.t('hospitality_activities.field_location')}
                      placeholder={i18n.t('hospitality_activities.field_location_placeholder')}
                      maxCaracteres={255}
                      value={formData.location}
                      change={(value: string) => handleInputChange('location', value)}
                      smalltext={i18n.t('hospitality_activities.field_location_hint')}
                    />
                  </div>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'hour',
                      }}
                      label={i18n.t('hospitality_activities.field_period')}
                      placeholder={i18n.t('hospitality_activities.field_period_placeholder')}
                      maxCaracteres={50}
                      value={formData.period}
                      change={(value: string) => handleInputChange('period', value)}
                    />
                  </div>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'number',
                      }}
                      label={i18n.t('hospitality_activities.field_capacity')}
                      placeholder={i18n.t('hospitality_activities.field_capacity_placeholder')}
                      maxCaracteres={50}
                      value={formData.capacity}
                      change={(value: string) => handleInputChange('capacity', value)}
                      smalltext={i18n.t('hospitality_activities.field_capacity_hint')}
                    />
                  </div>
                </div>

                <div className='col-md-6'>
                  <Input
                    inputAttr={{
                      type: 'number',
                    }}
                    label={i18n.t('hospitality_activities.field_order')}
                    placeholder={i18n.t('hospitality_activities.field_order_placeholder')}
                    value={formData.order}
                    change={(value: string) => handleInputChange('order', value)}
                  />
                </div>

                <div className='row'>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'select',
                      }}
                      label={i18n.t('hospitality_activities.field_category')}
                      options={Object.values(ActivityCategory).map((category) => ({
                        value: category,
                        select: getCategoryLabel(category),
                      }))}
                      value={formData.category}
                      change={(value: ActivityCategory) => handleInputChange('category', value)}
                    />
                  </div>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'select',
                      }}
                      label={i18n.t('hospitality_activities.field_age_group')}
                      options={Object.values(AgeGroup).map((ageGroup) => ({
                        value: ageGroup,
                        select: getAgeGroupLabel(ageGroup),
                      }))}
                      value={formData.ageGroup}
                      change={(value: AgeGroup) => handleInputChange('ageGroup', value)}
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-md-6'>
                    <TitleFreePriceInput
                      value={formData.titleFreePrice || ''}
                      onChange={(value) => handleInputChange('titleFreePrice', value || '')}
                      label='Título atividade sem custo'
                      placeholder='Ex:. Cortesia do Hotel'
                      maxCaracteres={30}
                      disabled={parseFloat(formData.price) !== 0 && formData.price !== ''}
                      toolTip={true}
                      toolTiptext='Se o preço estiver zerado, este texto poderá ser exibido no WebApp. Se vazio, nada será mostrado.'
                      toolTipIcon='info-circle'
                    />
                  </div>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'number',
                      }}
                      label={i18n.t('hospitality_activities.field_price')}
                      placeholder={i18n.t('hospitality_activities.field_price_placeholder')}
                      value={formData.price}
                      change={(value: string) => handleInputChange('price', value)}
                      smalltext={i18n.t('hospitality_activities.field_price_hint')}
                      minNumber={0}
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'checkbox',
                      }}
                      label={i18n.t('hospitality_activities.field_requires_reservation')}
                      checkSwift={true}
                      checked={formData.requiresReservation}
                      change={(checked: boolean) =>
                        handleInputChange('requiresReservation', checked)
                      }
                      smalltext={i18n.t('hospitality_activities.field_requires_reservation_hint')}
                    />
                  </div>
                  <div className='col-md-6'>
                    <Input
                      inputAttr={{
                        type: 'checkbox',
                      }}
                      label={i18n.t('hospitality_activities.field_is_active')}
                      checkSwift={true}
                      checked={formData.isActive}
                      change={(checked: boolean) => handleInputChange('isActive', checked)}
                      smalltext={i18n.t('hospitality_activities.field_is_active_hint')}
                    />
                  </div>
                </div>

                <div className='d-flex justify-content-end'>
                  <Button
                    text={i18n.t(`buttons.cancel`)}
                    color='danger'
                    size='small'
                    horizontalPadding={8}
                    btnAttr={{
                      type: 'button',
                    }}
                    click={() => navigate('/hospitality/hotel-activities')}
                    btnClass='me-3'
                  />
                  <Button
                    text={i18n.t(`buttons.create`)}
                    color='primary'
                    iconColor='#FFF'
                    icon='fas fa-save'
                    size='small'
                    horizontalPadding={8}
                    btnAttr={{
                      type: 'button',
                    }}
                    click={createActivity}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const CreateHotelActivity: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {title: 'Atividades do Hotel', path: 'hotel-activities', isActive: false},
          {title: 'Criar Atividade', path: 'hotel-activities/criar', isActive: true},
        ]}
      >
        Criar Atividade do Hotel
      </PageTitle>
      <CreateHotelActivityContent />
    </>
  )
}

export {CreateHotelActivity}
